<script setup lang="ts">
import ButtonPrimary from '@buttons/button-primary.vue';
import PartIconCircle from '@parts/part-icon-circle.vue';
import CoreIcon from '@core/core-icon.vue';

import { computed } from 'vue';
import { useCode } from '@composables/code';
import {useToast} from "vue-toast-notification";
import {useI18n} from "vue-i18n";
import {shareURL} from "@tma.js/sdk-vue";
import isInTelegram from "@/utils/isInTelegram";

const { share, copy } = useCode();
const props = defineProps({
	title: {
		type: String,
		default: ''
	},
	message: {
		type: String,
		default: ''
	},
	link: {
		type: String,
		default: ''
	},
	count: {
		type: Number,
		default: 0
	},
	paid: {
		type: Number,
		default: 0
	},
});

const { t } = useI18n();
const toast = useToast();

const shareData = computed(() => {
	return {
		title: props.message,
		url: props.link,
	};
});

const onShareHandler = () => {
	if (isInTelegram()) {
		shareURL.ifAvailable(
			props.link,
			props.message,
		);
	} else {
		navigator.share(shareData.value);
	}
}

const onCopyHandler = async () => {
  copy(props.link).then(() => {
    toast.info(t('common.toast.copied_to_clipboard'), {
      position: 'top'
    });
  })
}
</script>

<template>
	<div class="part-invite-box">
		<div class="part-invite-box__form">
			<div v-if="title" class="part-invite-box__form-title font-18-pro-medium">{{ title }}</div>
			<div v-if="link" class="part-invite-box__form-link">{{ link }}</div>
			<div class="part-invite-box__form-bottom">
				<button-primary
					class="part-invite-box__form-button"
					@click.native="onShareHandler"
				>
					{{ $t('invite.share') }}
				</button-primary>
				<part-icon-circle
					white
					class="part-invite-box__form-copy"
					:icon="{name: 'copy', viewBox: '0 0 18 18'}"

					@click.native="onCopyHandler"
				>
				</part-icon-circle>
			</div>
		</div>
    <div class="part-invite-box__info">
		<div class="part-invite-box__info-icon">
			<core-icon name="wallet2" viewBox="0 0 24 24" />
		</div>
		<div class="part-invite-box__info-title">{{ $t('invite.months_gift', paid) }}</div>
		<div class="part-invite-box__info-invited">{{ $t('invite.months_count', paid) }}</div>
    </div>
		<div class="part-invite-box__info">
			<div class="part-invite-box__info-icon">
				<core-icon name="users" viewBox="0 0 32 20" />
			</div>
			<div class="part-invite-box__info-title">{{ $t('invite.invited', count) }}</div>
			<div class="part-invite-box__info-invited">{{ $t('invite.invited_count', count) }}</div>
		</div>
	</div>
</template>

<style lang="scss">
.part-invite-box {

	&__form {
		padding: 2.4rem;
		background: var(--color-panel);
		border: 1px solid var(--color-panel);
		border-radius: 2.4rem;
	}
	&__form-title {
		margin-bottom: 1rem;
	}
	&__form-link {
		opacity: 0.5;
		margin-bottom: 2.4rem;
	}
	&__form-copy {
		flex: 0 0 auto;
		--button-size: 5.2rem;
	}
	&__form-bottom {
		display: flex;
		gap: 0.8rem;
		@include min-desktop {
			gap: 1rem;
		}
	}
	&__form-button {
		flex: 1 1 auto;
		min-width: 0;
	}
	&__info {
		padding: 1.6rem 2.1rem;
		border-radius: 1.6rem;
		margin-top: 0.8rem;
		background: var(--color-panel);
		border: 1px solid var(--color-panel);
		display: flex;
		align-items: center;
		gap: 1.6rem;
		@include min-desktop {
			@include font-18-pro-medium;
		}
	}

	&__info-icon {
		fill: var(--color-active);
		width: 3.2rem;
		display: flex;
		justify-content: center;
		align-items: center;

		svg {
			width: 100%;
			&.icon-wallet2 {
				width: 2.4rem;
			}
		}
	}
	&__info-invited {
		margin-left: auto;
		color: var(--color-active);
	}
}
</style>

